import { FormState, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/src/components/ui/form";
import { useSubscribe } from "@/src/lib/actions";
import { emptyYachtsFiltersForm, YachtsFiltersFormT } from "./yachts-filters.schema";
import { useCallback } from "react";
import { CharterTypes } from "./CharterTypes";
import { Separator } from "@/src/components/ui/separator";
import { API } from "./API";
import { YachtID } from "./YachtID";
import { Operator } from "./Operator";
import { useOffersStore } from "../../../../stores/offersStore";
import { YachtDetails } from "./YachtDetails";
import { Price } from "./Price";
import { Amenitites } from "./Amenitites";
import { Manufacturers } from "./Manufacturers";
import { Model } from "./Model";
import { APIFilters } from "../YachtsListing";
import { YachtName } from "./YachtName";
import { MainsailType } from "./MainsailType";
import { APIStatuses } from "./APIStatuses";
import { Brand } from "./Brand";

export function YachtsFiltersForm({ onSave, apiFilters }: { onSave: () => void; apiFilters: APIFilters }) {
  const { yachtsFilters, setYachtsFilters } = useOffersStore();
  // The /filters endpoint derives these bounds from SQL MIN/MAX aggregates, which
  // come back null over an empty / all-null set (e.g. crew, bathrooms or operator
  // rating with no data). A null (or NaN) sitting in a z.number() field fails the
  // WHOLE form's zod validation, and onSubmit's error branch only console.logs — so
  // "Show yachts" silently did nothing and no filter could be applied. Keep only
  // finite numeric defaults; dropped fields fall back to unset (schema-optional).
  const safeApiFilters = Object.fromEntries(
    Object.entries(apiFilters).filter(([, v]) => typeof v === "number" && Number.isFinite(v)),
  ) as Partial<APIFilters>;
  const form = useForm<YachtsFiltersFormT>({
    resolver: zodResolver(YachtsFiltersFormT),
    defaultValues: { ...emptyYachtsFiltersForm, ...safeApiFilters },
    ...(yachtsFilters ? { values: { ...emptyYachtsFiltersForm, ...safeApiFilters, ...yachtsFilters } } : {}),
    resetOptions: {
      keepDefaultValues: true,
    },
  });
  const searchFilters = {
    ...(yachtsFilters?.people && { people: yachtsFilters?.people }),
    ...(yachtsFilters?.location && { location: yachtsFilters?.location }),
    ...(yachtsFilters?.region && { region: yachtsFilters?.region }),
    ...(yachtsFilters?.country && { country: yachtsFilters?.country }),
    ...(yachtsFilters?.tlr && { tlr: yachtsFilters?.tlr }),
    ...(yachtsFilters?.model && { model: yachtsFilters?.model }),
    ...(yachtsFilters?.checkIn && { checkIn: yachtsFilters?.checkIn }),
    ...(yachtsFilters?.checkOut && { checkOut: yachtsFilters?.checkOut }),
    ...(yachtsFilters?.boatType && { boatType: yachtsFilters?.boatType }),
  };

  const onClear = () => {
    const filters = {
      ...emptyYachtsFiltersForm,
      ...safeApiFilters,
      ...searchFilters,
    };
    form.reset(filters);
    setYachtsFilters(searchFilters);
    onSave();
  };

  // Filters are all OPTIONAL. This used to run through form.handleSubmit and its
  // invalid branch only console.logged — so one stray invalid value (a null numeric
  // bound from /filters, or a NaN price from a currency rate that wasn't loaded yet)
  // failed zod and silently disabled "Show yachts" with zero feedback, blocking the
  // whole filter panel. Apply the broker's selection unconditionally and close;
  // findCommonFormProps keeps only truthy dirty fields, so any null/NaN is dropped
  // rather than sent to search. Validation issues are logged, not blocking.
  const onSubmit = useCallback(
    (e?: { preventDefault?: () => void }) => {
      e?.preventDefault?.();
      const data = form.getValues();
      const issues = YachtsFiltersFormT.safeParse(data).error?.issues;
      if (issues?.length) console.warn("YachtsFiltersForm: applying filters despite validation issues", issues);
      const searchDirties = Object.keys(searchFilters)?.map((k) => ({ [k]: true }));
      const dirtyFields = findCommonFormProps(
        { ...data, ...searchFilters },
        { ...form.formState.dirtyFields, ...Object.assign({}, ...searchDirties) },
      );
      setYachtsFilters(dirtyFields);
      onSave();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form],
  );

  useSubscribe("filters:save", onSubmit);
  useSubscribe("filters:clear", onClear);

  return (
    <Form {...form}>
      <form className="flex flex-col gap-6" onSubmit={onSubmit}>
        <Brand />
        <Separator />
        <CharterTypes />
        <Separator />
        <APIStatuses />
        <Separator />
        <div className="grid justify-between gap-x-4 grid-cols-2">
          <Operator />
          <YachtID />
        </div>
        <Separator />
        <YachtName />
        <Separator />
        <API />
        <Separator />
        <Price priceMaxDefault={apiFilters.priceMax} priceMinDefault={apiFilters.priceMin} />
        <Separator />
        <YachtDetails {...apiFilters} />
        <Separator />
        <MainsailType />
        <Separator />
        <Manufacturers />
        <Separator />
        <Model />
        <Separator />
        <Amenitites />
      </form>
    </Form>
  );
}

export function findCommonFormProps(
  data: YachtsFiltersFormT,
  dirties: FormState<YachtsFiltersFormT>["dirtyFields"],
): Partial<YachtsFiltersFormT> {
  const commonProps: Partial<YachtsFiltersFormT> = {};
  for (const [key, value] of Object.entries(dirties)) {
    if ((Array.isArray(value) && !!value?.length) || (!Array.isArray(value) && !!value)) {
      (commonProps as any)[key] = (data as any)[key];
    }
  }

  commonProps.apiStatus = data.apiStatus;

  return commonProps;
}
